/**
 * Reusable component for displaying phone numbers with badge-style labels for roles and phone types.
 * @agentic @benchmark/phone
 */

// Components
import { Box, Group, Badge, Anchor, Text, Stack } from "@mantine/core";
import { IconPhone } from "@tabler/icons-react";

// Utils
import { formatPhoneNumber } from "@benchmark/core/utils";

import styles from "./PhoneNumberDisplay.module.css";

/**
 * Props for PhoneNumberDisplay component.
 * @agentic @benchmark/phone
 */
export type PhoneNumberDisplayProps = {
  phoneNumber: string;
  phoneType?: string;
  roleLabel?: string;
  isPrimary?: boolean;
  contactName?: string;
  onCallClick?: () => void;
  ariaLabel?: string;
  className?: string;
  /** When false, omits the leading phone icon (e.g. parent row shows one icon). */
  showIcon?: boolean;
  /**
   * `profileCard`: compact list — patient numbers show digits + type + Primary only (no repeated “Patient” label);
   * related contacts: number aligned like patient lines; only the name/relationship row is inset with a brand purple rule.
   * `default`: stacked name + number layout.
   */
  layout?: "default" | "profileCard";
};

/**
 * Displays a formatted phone number with optional role badge and call link.
 * @agentic @benchmark/phone
 */
export const PhoneNumberDisplay = ({
  phoneNumber,
  phoneType = "Phone",
  roleLabel,
  contactName,
  isPrimary,
  onCallClick,
  ariaLabel,
  className,
  showIcon = true,
  layout = "default",
}: PhoneNumberDisplayProps) => {
  const displayName = contactName || "Patient";
  const formattedNumber = formatPhoneNumber(phoneNumber, {
    style: "national",
    extDisplay: "x",
  });
  const formattedLine = `${formattedNumber} (${phoneType})`;

  const handleClick = () => {
    if (onCallClick) {
      onCallClick();
    }
  };

  const textSizeMd = "md" as const;
  const textSizeSm = "sm" as const;
  const textSize = layout === "profileCard" ? textSizeSm : textSizeMd;

  const relatedContactSummary = contactName
    ? `${formattedLine}, contact ${contactName}${roleLabel ? `, ${roleLabel}` : ""}`
    : "";

  const ariaForStatic =
    ariaLabel ||
    (contactName
      ? `Phone number for ${contactName}: ${formattedNumber}`
      : `Phone number: ${formattedNumber}`);

  const ariaForCall = ariaLabel || `Call ${displayName} at ${formattedNumber}`;

  const numberBlock = onCallClick ? (
    <Anchor
      onClick={handleClick}
      className={styles.callLink}
      aria-label={ariaForCall}
    >
      <Text size={textSize} component="span">
        {formattedLine}
      </Text>
    </Anchor>
  ) : (
    <Text size={textSize} aria-label={ariaForStatic}>
      {formattedLine}
    </Text>
  );

  if (layout === "profileCard" && !contactName) {
    return (
      <Box className={className}>
        <Group gap="xs" align="center" wrap="wrap">
          {numberBlock}
          {isPrimary && (
            <Badge
              size="sm"
              variant="light"
              color="purple"
              aria-label="Primary"
            >
              Primary
            </Badge>
          )}
        </Group>
      </Box>
    );
  }

  if (layout === "profileCard" && contactName) {
    const identityLine = roleLabel
      ? `${contactName}, ${roleLabel}`
      : contactName;

    return (
      <Box
        className={className}
        role="group"
        aria-label={ariaLabel ?? relatedContactSummary}
      >
        <Stack gap={4}>
          <Box>{numberBlock}</Box>
          <Box className={styles.relatedContactMeta}>
            <Text size={textSize} c="dimmed">
              {identityLine}
            </Text>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <Group gap="xs" align="top" wrap="nowrap">
        {showIcon && (
          <IconPhone size={16} color="var(--mantine-color-gray-6)" />
        )}
        <Box className={styles.body}>
          <Group gap="xs" align="center" wrap="nowrap">
            <Text size="md" fw={600}>
              {displayName}
              {roleLabel && ` (${roleLabel})`}
            </Text>

            {isPrimary && (
              <Badge
                size="sm"
                variant="light"
                color="purple"
                aria-label="Primary"
              >
                Primary
              </Badge>
            )}
          </Group>
          <Box>
            {onCallClick ? (
              <Anchor
                onClick={handleClick}
                className={styles.callLink}
                aria-label={ariaForCall}
              >
                <Text size="md">{formattedLine}</Text>
              </Anchor>
            ) : (
              <Text size="md" aria-label={ariaForStatic}>
                {formattedLine}
              </Text>
            )}
          </Box>
        </Box>
      </Group>
    </Box>
  );
};
